import React from 'react';

import ElapsedTimeEventTracker from 'analytics/ElapsedTimeEventTracker';
import {RESULTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ErrorTriangle, {TriangleLocation, TriangleState} from 'notebook/tables/errors/ErrorTriangle';
import {AssayValidationHistory, ReadableAssayValidationStatus} from 'results/validation/ValidationConstants';

import * as AssayRecordConstants from '../assays/AssayRecordConstants';

const PRIOR_CHANGES_SYSTEM_MESSAGE = 'Has prior validation changes';

export function statusToTriangleState(
  status: ReadableAssayValidationStatus,
  comment: string | null,
  history?: AssayValidationHistory | null
): TriangleState {
  const hasAnyPriorChanges =
    history &&
    history.filter(
      (validationChange) =>
        validationChange.comment || validationChange.status === AssayRecordConstants.INVALID
    ).length > 0;

  if (status === ReadableAssayValidationStatus.INVALID) {
    return TriangleState.INVALID;
  } else if (status === ReadableAssayValidationStatus.PARTIALLY_VALID) {
    return TriangleState.PARTIALLY_VALID;
  } else if (comment || hasAnyPriorChanges) {
    return TriangleState.VALID_WITH_TRIANGLE;
  } else {
    return TriangleState.VALID_NO_TRIANGLE;
  }
}

const STATUS_TOOLTIP_TITLES: {[state in TriangleState]?: string} = {
  [TriangleState.VALID_WITH_TRIANGLE]: 'Valid',
  [TriangleState.INVALID]: 'Invalid',
  [TriangleState.PARTIALLY_VALID]: 'Partially Valid',
};

function _getTooltipMessage(
  tristate: TriangleState,
  currentComment: string | null,
  location: TriangleLocation
): string {
  if (tristate === TriangleState.VALID_WITH_TRIANGLE && location === TriangleLocation.CELL) {
    return currentComment || PRIOR_CHANGES_SYSTEM_MESSAGE;
  }
  return currentComment || '';
}

interface ValidationErrorTriangleProps {
  status: ReadableAssayValidationStatus;
  comment: string | null;
  history?: AssayValidationHistory | null;
  location: TriangleLocation;
  onClick: () => void;
}

export default class ValidationErrorTriangle extends React.Component<ValidationErrorTriangleProps> {
  eventTracker: ElapsedTimeEventTracker;

  constructor(props: ValidationErrorTriangleProps) {
    super(props);
    this.eventTracker = new ElapsedTimeEventTracker(RESULTS_ANALYTICS_ACTIONS.HOVERED_VALIDATION_TRIANGLE);
  }

  override render(): JSX.Element | null {
    const {status, comment, history, location, onClick} = this.props;
    const tristate: TriangleState = statusToTriangleState(status, comment, history);
    return (
      <ErrorTriangle
        location={location}
        error={{
          type: STATUS_TOOLTIP_TITLES[tristate] || '',
          message: _getTooltipMessage(tristate, comment, location),
        }}
        onClick={onClick}
        onMouseEnter={() => {
          this.eventTracker.startTimer();
        }}
        onMouseLeave={() => {
          this.eventTracker.stopTimerAndTrack();
        }}
        state={tristate}
        dataTestAttrs={{component: 'ValidationTriangle', element: 'triangle'}}
      />
    );
  }
}
